import React, { useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  BoxProps,
  Drawer,
  DrawerProps,
  Collapse,
  LinearProgress,
  LinearProgressProps,
  Toolbar,
  IconButton,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import NavigationBarHeader from '../UserPages/UserDashboard/components/NavigationBarHeader';
import QuestionsListView from './QuestionsListView';
import InvalidRoute from '../Validate/InvalidRoute';
import DrawerContents, {
  drawerWidth,
} from '../UserPages/UserDashboard/components/DrawerContents';
import NavBarContents, {
  navBarCollapseWith,
  navBarWidth,
} from '../UserPages/UserDashboard/components/NavBarContents';
import { SafeAreaGaps, isMobileScreen } from '../../helper';
import checkUserNavigationBarEnabled from '../../navigation/helper';
import { TP_COLORS } from '../../constants/Color';
import { useAppSelector } from '../../hooks';
import { getThemeMode } from '../../store/ConfigSlice';
import { getIsLoading } from '../../store/LoaderSlice';

interface IState {
  isNavBarExpanded: boolean;
  barWidth: number;
}

const initialState: IState = {
  isNavBarExpanded: true,
  barWidth: navBarWidth,
};

const QuestionsPage: React.FC = () => {
  const location = useLocation();

  // Store
  const themeMode = useAppSelector(getThemeMode);
  const isLoading = useAppSelector(getIsLoading);

  // State
  const [state, setState] = useState<IState>(initialState);

  const isMobile = isMobileScreen();
  const isNavigationBarEnabled = checkUserNavigationBarEnabled(location);
  const mainStyles: BoxProps['sx'] = {
    flexGrow: 1,
    width: { sm: `calc(100% - ${isMobile ? drawerWidth : 0}px)` },
    backgroundColor:
      themeMode === 'light' ? TP_COLORS.paperGray.light : 'background.paper',
    color: 'text.primary',
    minHeight: '100vh',
  };

  const RENDER_ROUTES = () => {
    return (
      <Routes>
        <Route path="/:subTopic" element={<QuestionsListView />} />

        {/* Validate */}
        <Route path="*" element={<InvalidRoute />} />
      </Routes>
    );
  };

  if (isNavigationBarEnabled) {
    const loaderStyles: LinearProgressProps['sx'] = {
      position: 'fixed',
      zIndex: 1,
      top: `calc(${SafeAreaGaps.top} + 64px)`,
      marginLeft: SafeAreaGaps.left,
    };

    const navStyles: BoxProps['sx'] = {
      width: { sm: state.barWidth },
      flexShrink: { sm: 0 },
      transition: 'width 0.5s',
      visibility: { xs: 'hidden', sm: 'visible' },
    };

    const drawerStyles: DrawerProps['sx'] = {
      '& .MuiDrawer-paper': {
        boxSizing: 'border-box',
        width: state.barWidth,
        transition: 'width 0.5s',
      },
    };

    return (
      <Box>
        <CssBaseline />

        {/* Header */}
        <NavigationBarHeader
          appBarProps={{ sx: { display: { xs: 'none', sm: 'block' } } }}
        />
        <Toolbar sx={{ display: { xs: 'none', sm: 'block' } }} />

        {/* Loader */}
        <Collapse in={isLoading} sx={loaderStyles}>
          <LinearProgress color="info" />
        </Collapse>

        <Box sx={{ display: 'flex' }}>
          {!isMobile && (
            <Box component="nav" sx={navStyles}>
              <Drawer
                open
                variant="persistent"
                sx={drawerStyles}
                PaperProps={{
                  sx: {
                    backgroundColor:
                      themeMode === 'light' ? 'background.paper' : '#191d20',
                    top: '64px',
                    height: 'calc(100% - 64px)',
                    overflowY: 'auto',
                  },
                }}
              >
                <div
                  style={{
                    paddingTop: `calc(${SafeAreaGaps.top})`,
                    paddingBottom: `calc(${SafeAreaGaps.bottom} + 15px)`,
                    paddingLeft: `calc(${SafeAreaGaps.top} + 15px)`,
                    paddingRight: `calc(${SafeAreaGaps.right} + 15px)`,
                    height: '100%',
                  }}
                >
                  <div
                    style={{
                      visibility: state.isNavBarExpanded ? 'visible' : 'hidden',
                    }}
                  >
                    <NavBarContents isNavBarExpanded={state.isNavBarExpanded} />
                  </div>
                  <div
                    style={{
                      minWidth: '50px',
                      visibility: state.isNavBarExpanded ? 'hidden' : 'visible',
                    }}
                  />
                </div>
              </Drawer>
              <IconButton
                style={{
                  zIndex: 1200,
                  padding: '3px',
                  position: 'fixed',
                  top: '100px',
                  transition: 'all 0.5s',
                  left: `calc(${state.barWidth}px - 15px)`,
                }}
                sx={{
                  backgroundColor: 'background.default',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: TP_COLORS.white,
                  },
                }}
                onClick={() =>
                  setState((prevState) => ({
                    ...prevState,
                    isNavBarExpanded: !prevState.isNavBarExpanded,
                    barWidth:
                      prevState.barWidth === navBarWidth
                        ? navBarCollapseWith
                        : navBarWidth,
                  }))
                }
              >
                {state.isNavBarExpanded ? (
                  <ChevronLeftIcon />
                ) : (
                  <ChevronRightIcon />
                )}
              </IconButton>
            </Box>
          )}

          <Box component="main" sx={mainStyles}>
            {RENDER_ROUTES()}
          </Box>
        </Box>
      </Box>
    );
  }

  // Styles
  const loaderStyles: LinearProgressProps['sx'] = {
    position: 'fixed',
    zIndex: 1,
    top: SafeAreaGaps.top,
    marginTop: SafeAreaGaps.top,
    marginLeft: SafeAreaGaps.left,
    width: { sm: `calc(100% - ${isMobile ? drawerWidth : 0}px)` },
  };

  const navStyles: BoxProps['sx'] = {
    width: { sm: drawerWidth },
    flexShrink: { sm: 0 },
  };

  const drawerStyles: DrawerProps['sx'] = {
    display: { xs: 'none', sm: 'block' },
    '& .MuiDrawer-paper': {
      boxSizing: 'border-box',
      width: drawerWidth,
    },
  };

  // RENDER
  return (
    <div>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />

        {/* Drawer */}
        {!isMobile && (
          <Box component="nav" sx={navStyles}>
            <Drawer
              open
              variant="permanent"
              sx={drawerStyles}
              PaperProps={{
                sx: {
                  backgroundColor:
                    themeMode === 'light' ? 'background.paper' : '#191d20',
                },
              }}
            >
              <DrawerContents />
            </Drawer>
          </Box>
        )}

        {/* Body */}
        <Box component="main" sx={mainStyles}>
          <Collapse in={isLoading} sx={loaderStyles}>
            <LinearProgress color="info" />
          </Collapse>
          {RENDER_ROUTES()}
        </Box>
      </Box>
    </div>
  );
};

export default QuestionsPage;
